import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { unifiedJobStreamService } from '../services/unifiedJobStreamService'
import { updateJobFromSSE, registerJob, getToolMetadata } from '../redux/reducers/jobsReducer'
import { useLanguage } from '../utils/languageProvider'
import { getUserLanguage } from '../utils/localStorage'

/**
 * Global hook to initialize the Unified SSE Stream connection
 * and route SSE events into the Redux jobs store.
 */
export const useUnifiedJobStream = (enabled = true) => {
  const dispatch = useDispatch()
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated)

  let activeLanguage = 'en'
  try {
    const langContext = useLanguage ? useLanguage() : null
    if (langContext?.language) {
      activeLanguage = langContext.language
    } else if (typeof window !== 'undefined') {
      activeLanguage = getUserLanguage() || 'en'
    }
  } catch (e) {
    if (typeof window !== 'undefined') {
      activeLanguage = getUserLanguage() || 'en'
    }
  }

  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      unifiedJobStreamService.disconnect()
      return
    }

    // Connect to single unified SSE stream (will reconnect if language changed)
    unifiedJobStreamService.connect(false, activeLanguage)

    // Listen to incoming SSE messages and update Redux state
    const unsubscribe = unifiedJobStreamService.addListener((eventPayload) => {
      dispatch(updateJobFromSSE(eventPayload))
    })

    return () => {
      unsubscribe()
    }
  }, [enabled, isAuthenticated, dispatch, activeLanguage])

  const registerNewJob = (jobDetails) => {
    dispatch(registerJob(jobDetails))
  }

  return {
    registerNewJob,
    connect: () => {
      unifiedJobStreamService.connect(false, activeLanguage)
    },
    disconnect: () => unifiedJobStreamService.disconnect()
  }
}

/**
 * Tool-level hook to monitor a specific job by jobId or tool_name
 */
export const useJobTracker = (jobId, tool_name) => {
  const activeJobs = useSelector((state) => state.jobs?.activeJobs || {})

  let targetJob = null

  if (jobId) {
    let foundJob = activeJobs[jobId]
    if (!foundJob) {
      foundJob = Object.values(activeJobs).find(
        (j) => j.generationId === jobId || j.jobId === jobId || (Array.isArray(j.childIds) && j.childIds.includes(jobId))
      )
    }
    if (foundJob) {
      if (tool_name) {
        const expectedMeta = getToolMetadata(tool_name)
        const jobMeta = getToolMetadata(foundJob.tool_name)
        if (expectedMeta?.tool_name === jobMeta?.tool_name) {
          targetJob = foundJob
        }
      } else {
        targetJob = foundJob
      }
    }
  }

  if (!targetJob && tool_name) {
    const metaInfo = getToolMetadata(tool_name)
    const expectedToolName = metaInfo?.tool_name || tool_name
    
    // Normalize aliases for Talking Avatar & Action Generation tools
    const isTalkingAvatar =
      expectedToolName === 'photo_avatar_talking' ||
      expectedToolName === 'talking_avatar' ||
      tool_name === 'talking_avatar' ||
      tool_name === 'photo_avatar_talking'

    const isActionGeneration =
      expectedToolName === 'action_generation' ||
      tool_name === 'action_generation'

    const isFaceSwap =
      expectedToolName === 'face_swap' ||
      expectedToolName === 'video-face-swap' ||
      tool_name === 'face_swap' ||
      tool_name === 'video-face-swap' ||
      tool_name === 'face-swap' ||
      tool_name === 'video_face_swap'

    const isSceneSwap =
      expectedToolName === 'scene_swap' ||
      expectedToolName === 'background_change' ||
      expectedToolName === 'background-change' ||
      tool_name === 'scene_swap' ||
      tool_name === 'background_change'

    const isColorize =
      expectedToolName === 'colorize' ||
      expectedToolName === 'colorise' ||
      expectedToolName === 'color_mode_enhance' ||
      tool_name === 'colorize'

    const matches = Object.values(activeJobs).filter((j) => {
      if (isTalkingAvatar) {
        return (
          j.tool_name === 'photo_avatar_talking' ||
          j.tool_name === 'talking_avatar' ||
          j.tool_name === 'talkingavatar'
        )
      }
      if (isActionGeneration) {
        return (
          j.tool_name === 'action_generation' ||
          j.tool_name === 'action-generation'
        )
      }
      if (isFaceSwap) {
        return (
          j.tool_name === 'face_swap' ||
          j.tool_name === 'face-swap' ||
          j.tool_name === 'video-face-swap' ||
          j.tool_name === 'video_face_swap'
        )
      }
      if (isSceneSwap) {
        return (
          j.tool_name === 'scene_swap' ||
          j.tool_name === 'background_change' ||
          j.tool_name === 'background-change' ||
          j.tool_name === 'Scene Swap' ||
          j.tool_name === 'Background Change'
        )
      }
      if (isColorize) {
        return (
          j.tool_name === 'colorize' ||
          j.tool_name === 'colorise' ||
          j.tool_name === 'color_mode_enhance' ||
          j.tool_name === 'Colorize'
        )
      }
      return (
        j.tool_name === expectedToolName ||
        j.tool_name === tool_name ||
        j.tool_name?.replace(/[\s\-_]+/g, '_') === tool_name?.replace(/[\s\-_]+/g, '_') ||
        j.tool_name?.replace(/[\s\-_]+/g, '_') === expectedToolName?.replace(/[\s\-_]+/g, '_')
      )
    })

    if (matches.length > 0) {
      const activeMatch = matches.find((j) => j.status === 'queued' || j.status === 'processing')
      if (activeMatch) {
        targetJob = activeMatch
      } else if (jobId) {
        const unviewedMatch = matches.find(
          (j) =>
            (j.status === 'completed' || j.status === 'failed' || j.status === 'rejected' || Boolean(j.error)) &&
            !j.viewed &&
            !j.dismissed
        )
        if (unviewedMatch) {
          targetJob = unviewedMatch
        }
      }
    }
  }

  const childResultsList = Object.values(targetJob?.childResults || {})
  const childProgressValues = childResultsList
    .map((c) => c.progress)
    .filter((p) => typeof p === 'number' && !isNaN(p) && p >= 0)

  const effectiveProgress =
    targetJob?.progress !== undefined && targetJob?.progress !== null
      ? targetJob.progress
      : (childProgressValues.length > 0 ? childProgressValues[0] : 0)

  return {
    job: targetJob,
    status: targetJob?.status || 'idle',
    progress: effectiveProgress,
    statusMessage: targetJob?.statusMessage || (childResultsList.length > 0 ? childResultsList[0]?.statusMessage : '') || '',
    output_url: targetJob?.output_url || null,
    error: targetJob?.error || null,
    isGenerating: targetJob?.status === 'queued' || targetJob?.status === 'processing'
  }
}
